// SEO工具类 - 用于动态设置页面标题和meta信息

/**
 * 【统一品牌常量】
 * 全站页面标题后缀、og:site_name、结构化数据站点名，一律引用 SITE_NAME，
 * 避免各页面标题后缀割裂（历史上曾混用多种不一致的后缀写法）。
 */
export const SITE_NAME = '云语匀雨的个人小站';
export const SITE_DESCRIPTION = '本网站为个人非经营性网站，分享自用小工具与学习笔记，所有内容仅用于个人学习与交流，不提供任何交易或商业服务。';

export class SEOManager {
  static setPageMeta(options = {}) {
    const {
      title = SITE_NAME,
      description = SITE_DESCRIPTION,
      keywords = '个人小站,工具分享,学习笔记,非经营性网站,云语匀雨',
      ogTitle,
      ogDescription,
      ogImage = 'https://4yshop.top/img/logo.png',
      canonical
    } = options;

    // 设置页面标题
    document.title = title;

    // 设置或更新meta标签
    this.setMetaTag('description', description);
    this.setMetaTag('keywords', keywords);

    // 设置Open Graph标签
    this.setMetaProperty('og:title', ogTitle || title);
    this.setMetaProperty('og:description', ogDescription || description);
    this.setMetaProperty('og:image', ogImage);
    this.setMetaProperty('og:url', window.location.href);
    this.setMetaProperty('og:type', 'website');
    this.setMetaProperty('og:site_name', SITE_NAME);
    this.setMetaProperty('og:locale', 'zh_CN');

    // 设置Twitter Card标签
    this.setMetaTag('twitter:card', 'summary_large_image');
    // 个人站点无需关联品牌账号，使用个人标识
    this.setMetaTag('twitter:site', '@yunyu_personal');
    this.setMetaTag('twitter:title', ogTitle || title);
    this.setMetaTag('twitter:description', ogDescription || description);
    this.setMetaTag('twitter:image', ogImage);
    this.setMetaTag('twitter:url', window.location.href);

    // 设置canonical链接
    if (canonical) {
      this.setCanonicalLink(canonical);
    }
  }

  static setMetaTag(name, content) {
    // 性能优化：只在内容发生变化时才更新
    let meta = document.querySelector(`meta[name="${name}"]`);
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', name);
      document.head.appendChild(meta);
    }

    // 避免不必要的DOM操作
    if (meta.getAttribute('content') !== content) {
      meta.setAttribute('content', content);
    }
  }

  static setMetaProperty(property, content) {
    // 性能优化：只在内容发生变化时才更新
    let meta = document.querySelector(`meta[property="${property}"]`);
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('property', property);
      document.head.appendChild(meta);
    }

    // 避免不必要的DOM操作
    if (meta.getAttribute('content') !== content) {
      meta.setAttribute('content', content);
    }
  }

  static setCanonicalLink(href) {
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', href);
  }

  // 预定义的页面SEO配置
  static getPageConfig(pageName, params = {}) {
    const configs = {
      home: {
        title: `免费实用的个人小工具与技术学习笔记 - ${SITE_NAME}`,
        description: `这里是云语匀雨的个人小站，记录自用电脑小工具、编程技术学习笔记与日常折腾过程，所有内容仅供学习交流。`,
        keywords: '个人小站,工具分享,学习笔记,非经营性网站,云语匀雨',
        canonical: 'https://4yshop.top/'
      },
      allapp: {
        title: `全部工具与实用小工具 - ${SITE_NAME}`,
        description: '云语匀雨整理并记录各类自用小工具、系统工具、办公工具、网络工具与多媒体程序，附带纯净高速的获取方式与体验记录。',
        keywords: '工具列表,自用工具,个人小站,工具合集',
        canonical: 'https://4yshop.top/allapp'
      },
      fenlei: {
        title: `工具分类与工具大全 - ${SITE_NAME}`,
        description: '按照系统工具、办公工具、多媒体、网络工具、美化、安全、开发及游戏等分类浏览云语匀雨整理的实用工具合集。',
        keywords: '工具分类,工具合集,个人小站',
        canonical: 'https://4yshop.top/fenlei'
      },
      search: {
        title: `站内搜索与工具检索 - ${SITE_NAME}`,
        description: '在本站快速搜索我记录的各类实用工具、自建服务与技术博客文章。',
        keywords: '站内搜索,工具搜索,个人小站',
        canonical: 'https://4yshop.top/search'
      },
      article: {
        title: `文章中心与技术笔记 - ${SITE_NAME}`,
        description: '欢迎访问云语匀雨的文章中心，这里汇集了关于编程开发、Java、Vue3、Linux运维、自建服务等精选技术笔记、使用教程与学习心得，记录并分享个人技术实践经验。',
        keywords: '技术笔记,学习笔记,个人博客,教程分享,云语匀雨',
        canonical: 'https://4yshop.top/article'
      },
      // 【2026-10-30 补】ArticleList.vue 用的是 articlelist key（原先缺失，兑底到 home 标题错误）
      articlelist: {
        title: `文章中心与技术笔记 - ${SITE_NAME}`,
        description: '欢迎访问云语匀雨的文章中心，这里汇集了关于编程开发、Java、Vue3、Linux运维、自建服务等精选技术笔记、使用教程与学习心得，记录并分享个人技术实践经验。',
        keywords: '技术笔记,学习笔记,个人博客,教程分享,云语匀雨',
        canonical: 'https://4yshop.top/article'
      },
      // 【2026-10-30 全面覆盖】以下页面原先无 SEO 配置，路由跳转后标题停留上一页
      ranks: {
        title: `荣誉殿堂 - ${SITE_NAME}`,
        description: '汇聚社区杰出贡献者、活跃先锋与热心赞助人的荣誉排行榜。',
        keywords: '排行榜,荣誉殿堂,贡献榜,个人小站',
        canonical: 'https://4yshop.top/ranks'
      },
      channel: {
        title: `社区频道 - ${SITE_NAME}`,
        description: '创建或加入社区频道，与大家交流分享。',
        keywords: '社区频道,频道,交流,个人小站',
        canonical: 'https://4yshop.top/channel'
      },
      chat: {
        title: `私信 - ${SITE_NAME}`,
        description: '与站内用户私信交流。',
        keywords: '私信,聊天,个人小站',
        canonical: 'https://4yshop.top/chat'
      },
      userprofile: {
        title: `${params.userName || '用户'} 的个人主页 - ${SITE_NAME}`,
        description: `查看用户 ${params.userName || ''} 的个人主页与分享内容。`,
        keywords: '用户主页,个人空间,个人小站',
        canonical: `https://4yshop.top/user/${params.userId || ''}`
      },
      // 【2026-10-30 补】MyInfo.vue 用的是 profile key（原先缺失，兑底到 home 标题错误）
      profile: {
        title: `个人中心 - ${SITE_NAME}`,
        description: '管理你的个人信息、收藏与发布内容。',
        keywords: '个人中心,用户信息,收藏,个人小站',
        canonical: 'https://4yshop.top/myinfo'
      },
      qqcallback: {
        title: `登录中 - ${SITE_NAME}`,
        description: '正在完成登录，请稍候。',
        keywords: '登录,个人小站',
        canonical: 'https://4yshop.top/'
      },
      wxlogin: {
        title: `登录中 - ${SITE_NAME}`,
        description: '正在完成登录，请稍候。',
        keywords: '登录,个人小站',
        canonical: 'https://4yshop.top/'
      },
      appdetails: {
        title: `${params.appName || '工具详情'} - ${SITE_NAME}`,
        description: `个人使用记录：${params.appName || '工具'}的基本信息与体验。`,
        keywords: `${params.appName || '工具'},个人使用,工具记录,个人小站`,
        canonical: `https://4yshop.top/appdetails/${params.id || ''}`
      },
      articledetails: {
        title: `${params.articleTitle || '文章详情'} - ${SITE_NAME}`,
        description: `阅读《${params.articleTitle || '文章'}》，这是我的个人学习记录。`,
        keywords: `${params.articleTitle || '文章'},学习笔记,个人小站`,
        canonical: `https://4yshop.top/article/${params.id || ''}`
      },
      fankui: {
        title: `意见反馈 - ${SITE_NAME}`,
        description: '欢迎为我的个人小站提出建议与反馈，用于改进体验。',
        keywords: '意见反馈,建议,个人小站',
        canonical: 'https://4yshop.top/fankui'
      },
      login: {
        title: `登录 - ${SITE_NAME}`,
        description: '登录以使用个人小站的收藏与记录功能。',
        keywords: '用户登录,账户登录,个人小站',
        canonical: 'https://4yshop.top/login'
      },
      register: {
        title: `注册 - ${SITE_NAME}`,
        description: '注册以保存你的收藏与互动，本站为个人非经营性。',
        keywords: '用户注册,账户注册,个人小站',
        canonical: 'https://4yshop.top/register'
      },
      rules: {
        title: `使用约定 - ${SITE_NAME}`,
        description: '了解本站的使用约定与社区规范，非经营性个人站。',
        keywords: '使用约定,社区规范,个人小站',
        canonical: 'https://4yshop.top/rules'
      },
      mianze: {
        title: `免责声明 - ${SITE_NAME}`,
        description: '查看本站免责声明，内容仅用于个人学习交流。',
        keywords: '免责声明,非经营性,个人小站',
        canonical: 'https://4yshop.top/mianze'
      },
      articleedit: {
        title: `编辑文章 - ${SITE_NAME}`,
        description: '编辑并发布我的学习笔记与技术记录。',
        keywords: '编辑文章,学习笔记,个人小站',
        canonical: 'https://4yshop.top/article/edit'
      },
      myinfo: {
        title: `个人中心 - ${SITE_NAME}`,
        description: '管理你的个人信息、收藏与发布内容。',
        keywords: '个人中心,用户信息,收藏,个人小站',
        canonical: 'https://4yshop.top/myinfo'
      },
      shop: {
        title: `个性化商城 - ${SITE_NAME}`,
        description: '使用积分兑换精美皮肤、头像框、气泡框和炫彩昵称，展示你的独特个性。',
        keywords: '个性化商城,皮肤兑换,气泡框,头像框,炫彩昵称,积分商城',
        canonical: 'https://4yshop.top/shop'
      },
      error: {
        title: `页面未找到 - ${SITE_NAME}`,
        description: '抱歉，您访问的页面不存在。',
        keywords: '404,页面未找到,个人小站',
        canonical: 'https://4yshop.top/'
      }
    };

    return configs[pageName] || configs.home;
  }

  // 添加结构化数据
  static addStructuredData(data) {
    // 检查是否已存在相同类型的结构化数据，避免重复添加
    const dataType = data['@type'];
    const existingScript = document.querySelector(`script[type="application/ld+json"][data-type="${dataType}"]`);

    if (existingScript) {
      // 如果已存在，更新内容
      existingScript.textContent = JSON.stringify(data);
    } else {
      // 如果不存在，创建新的
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-type', dataType);
      script.textContent = JSON.stringify(data);
      document.head.appendChild(script);
    }
  }

  // 网站基础结构化数据
  static addWebsiteStructuredData() {
    const websiteData = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": SITE_NAME,
      "url": "https://4yshop.top",
      "description": "本网站为个人非经营性网站，分享自用小工具与学习笔记，内容仅用于个人学习与交流，不提供任何交易或商业服务。",
      "potentialAction": {
        "@type": "SearchAction",
        "target": "https://4yshop.top/search?q={search_term_string}",
        "query-input": "required name=search_term_string"
      }
    };

    this.addStructuredData(websiteData);
  }

  /**
   * 添加面包屑结构化数据
   * @param {Array} items - 面包屑项数组 { name: string, item: string (url) }
   */
  static addBreadcrumbStructuredData(items = []) {
    const breadcrumbData = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": items.map((item, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": item.name,
        "item": item.item.startsWith('http') ? item.item : `https://4yshop.top${item.item.startsWith('/') ? '' : '/'}${item.item}`
      }))
    };

    this.addStructuredData(breadcrumbData);
  }
}